import React, { useCallback, useContext, useState } from 'react';
import _ from 'underscore';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { Activity } from 'botframework-directlinejs';
import { type BotActivity } from '@bingads-webui/ads-copilot-shared-types';

import { StreamingStatusType } from '../botTypes';
import { DirectLineContext } from '../components/direct-line-provider/direct-line-context';
import { IAdsCopilotProps } from '../types';
import { Loading } from '../Loading';
import { StopGenerating } from '../StopGenerating';
import { getOAuthCardResourceUri } from './useSSO';
import { isTypingMessage, EVENT_NAME_ENGAGE_TROUBLESHOOTER, isTroubleshootingEvent, getIsFromUser, getIsGeneratingAnswersAnimation } from '../utils';
import { StopTroubleshooting } from '../StopTroubleshooting';

const IgnoreToggleTypes = ['messageReaction', 'endOfConversation'];

interface IUseLoadingProps {
  onReceiveActivity?: IAdsCopilotProps['onReceiveActivity'];
  renderBottomComponent?: IAdsCopilotProps['renderBottomComponent'];
  isCustomStream?: boolean;
  isStopGeneratingEnabled?: IAdsCopilotProps['isStopGeneratingEnabled'];
  isStopTroubleshootingEnabled?: IAdsCopilotProps['isStopTroubleshootingEnabled'];
  isRefreshUIEnabled?: boolean;
  isProgressIndicatorEnabled?: boolean;
}

export const shouldToggleLoading = (activity: Activity) => {
  const isIgnoredType = IgnoreToggleTypes.indexOf(activity.type) > -1;
  return !isIgnoredType && !isTroubleshootingEvent(activity);
};

export const shouldToggleTroubleShooting = (activity: Activity) => isTroubleshootingEvent(activity);

export function useLoading({
  onReceiveActivity,
  renderBottomComponent,
  isCustomStream,
  isStopGeneratingEnabled,
  isStopTroubleshootingEnabled,
  isRefreshUIEnabled,
  isProgressIndicatorEnabled,
}: IUseLoadingProps) {
  const [loading, setLoading] = useState(false);
  const [troubleshooting, setTroubleshooting] = useState(false);
  const [postedMessageId, setPostedMessageId] = useState<string | undefined>(undefined);
  const [continueGenerating, setContinueGenerating] = useState(true);
  const i18n = useContext(I18nContext) as I18nModel;
  const { directLine, userid, username } = useContext(DirectLineContext)!;

  const onReceiveActivityWithLoading = useCallback<NonNullable<IAdsCopilotProps['onReceiveActivity']>>((activity, currentActivities, dl) => {
    const messageActivity = activity as BotActivity;
    if (shouldToggleLoading(activity) && !isCustomStream && !messageActivity.isReplayedActivity) {
      if (getIsFromUser(activity, username)) {
        setLoading(true);
        if (_.get(messageActivity, 'name') !== 'stopResponding') {
          setContinueGenerating(true);
        }
        setPostedMessageId(activity.id);
      } else if (activity.type === 'message' && !isTypingMessage(activity) && getOAuthCardResourceUri(activity) == null) {
        if (messageActivity.value?.StreamingStatus === StreamingStatusType.InProgress
          || getIsGeneratingAnswersAnimation(activity)
        ) {
          setLoading(true);
        } else {
          setLoading(false);
        }
      }
    } else if (shouldToggleTroubleShooting(activity)) {
      setLoading(false);
      if (messageActivity.value?.troubleshootingValue?.troubleshootingStatus === 'InProgress') {
        setTroubleshooting(true);
      } else {
        setTroubleshooting(false);
      }
    }

    if (onReceiveActivity) {
      return onReceiveActivity(activity, currentActivities, dl);
    }
    return currentActivities;
  }, [isCustomStream, onReceiveActivity, username]);

  const onClickStopGenerating = useCallback(() => {
    const stopRespondingMessage = {
      type: 'event',
      replyToId: postedMessageId,
      name: 'stopResponding',
      from: {
        id: userid,
        name: username,
      },
    };
    setContinueGenerating(false);

    directLine?.postActivity(stopRespondingMessage as any).subscribe();
  }, [directLine, postedMessageId, userid, username]);

  const onClickStopTroubleshooting = useCallback(() => {
    const stopTroubleshootingMessage = {
      type: 'event',
      name: EVENT_NAME_ENGAGE_TROUBLESHOOTER,
      from: {
        id: userid,
        name: username,
      },
      value: {
        isForceStopTroubleshooting: true,
        Features: ['Troubleshooting'],
      },
    };

    directLine?.postActivity(stopTroubleshootingMessage as any).subscribe();
  }, [directLine, userid, username]);

  const renderBottomComponentWithLoading = useCallback<NonNullable<IAdsCopilotProps['renderBottomComponent']>>((currentActivities) => {
    if (loading) {
      if (isRefreshUIEnabled) {
        return null;
      }
      if (isStopGeneratingEnabled) {
        return (
          <StopGenerating onClickStopGenerating={onClickStopGenerating} />
        );
      }
      if (isProgressIndicatorEnabled) {
        return null;
      }

      return (
        <Loading loadingText={i18n.getString(_TL_('Generating answers...'))} />
      );
    }

    if (troubleshooting && isStopTroubleshootingEnabled) {
      if (isRefreshUIEnabled) {
        return null;
      }
      return (
        <StopTroubleshooting onClickStopTroubleshooting={onClickStopTroubleshooting} />
      );
    }

    if (renderBottomComponent) {
      return renderBottomComponent(currentActivities);
    }

    return null;
  }, [i18n, loading, onClickStopGenerating, renderBottomComponent, isStopGeneratingEnabled, troubleshooting, isStopTroubleshootingEnabled, isRefreshUIEnabled, isProgressIndicatorEnabled, onClickStopTroubleshooting]);

  return {
    onReceiveActivity: onReceiveActivityWithLoading,
    renderBottomComponent: renderBottomComponentWithLoading,
    isGeneratingAnswer: loading && continueGenerating,
    onClickStopGenerating,
    isTroubleshooting: troubleshooting,
    onClickStopTroubleshooting,
  };
}
